<script lang="ts">
	//Services
	import { postReq } from '../../../services/http';
	import { format } from '../../../services/i18n';
	//Helpers
	import { capFirstChar, passwordHasAllMandatorySymbols } from '../../../helpers/utilities';
	import { createAsyncAction } from 'src/helpers/async_action_guard';
	import { reloginAfterPasswordChange } from 'src/helpers/change_password_helpers';
	import { validateSignUpPassword } from 'src/helpers/signup_helpers';
	//Design system components
	import { Button, Input, Row, Col, toast } from '@Shelly/shelly-design-system';
	import type { InputPropsTypes } from '@Shelly/shelly-design-system';
	// Types
	import PasswordUtility from 'src/components/PasswordUtility.svelte';
	import type { ChangePasswordRequest } from '../../../types/requests/settings/user/change_password';

	let currentPassValue = '',
		currentPassError: string = '',
		newPassError: string = '';

	const { loading: isSaving, run: runSave } = createAsyncAction();

	let inputProps: {
		size: InputPropsTypes['size'];
		icon: InputPropsTypes['icon'];
		value: InputPropsTypes['value'];
		label: InputPropsTypes['label'];
		type: InputPropsTypes['type'];
		disabled: InputPropsTypes['disabled'];
		class: string;
		autocomplete: string;
	} = {
		icon: 'fas fa-key',
		size: 'm',
		class: '',
		disabled: false,
		value: '',
		label: 'new-password',
		type: 'password',
		autocomplete: 'new-password',
	};

	$: error = newPassError;

	const handleChangePassword = async (oldPass, newPass) => {
		const passwordError = validateSignUpPassword(newPass, $format);
		if (passwordError) {
			newPassError = passwordError;
			return;
		}

		await runSave(async () => {
			const res: ChangePasswordRequest = await postReq('/user/change_password', {
				old_password: window.btoa(oldPass),
				new_password: window.btoa(newPass),
			});

			if (res.isok) {
				await reloginAfterPasswordChange(newPass);
			} else if (res?.errors?.wrong_old_password) {
				currentPassError = $format('toast_error_password_change_wrong_current_pass', {
					default: 'Wrong password.',
				});
			} else if (res.errors?.spwd) {
				newPassError = $format('toast_error_password_change_same_password', {
					default: 'Choose a password you haven’t used before.',
				});
			} else {
				toast.open({ type: 'danger', title: $format('setting_err_toast') });
			}
		});
	};

	const clearErrors = () => {
		if (currentPassError) {
			currentPassError = '';
		}
		if (newPassError) {
			newPassError = '';
		}
	};
</script>

<Row>
	<Col xs="12" md="3">
		<Input
			aria-labelledby="input-ph5v21d"
			icon={'fas fa-lock'}
			label={capFirstChar($format('placeholder_current_password'))}
			bind:value={currentPassValue}
			type={'password'}
			error={currentPassError}
			on:click={() => clearErrors()}
			id="input-ph5v21d"
		/>
	</Col>
	<Col xs="12" md="3">
		<PasswordUtility
			bind:inputProps
			bind:error
			dataTestId="change-password-new-password-input"
			on:click={() => clearErrors()}
		/>
	</Col>
	<Col xs="12" md="3">
		<Button
			size="m"
			class="width-md-auto width-sm-full"
			disabled={$isSaving || !passwordHasAllMandatorySymbols(inputProps.value)}
			loading={$isSaving}
			on:click={() => handleChangePassword(currentPassValue, inputProps.value)}
		>
			{$format('button_save')}
		</Button>
	</Col>
</Row>
